import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { format, addDays } from 'date-fns';

const { FiShield, FiCreditCard, FiPlus, FiSave, FiX, FiTrash2, FiPlayCircle, FiPauseCircle, FiCalendar, FiCheck, FiMessageSquare } = FiIcons;

export default function Superadmin() {
  const { isSuperAdmin, user } = useAuth();
  const [activeTab, setActiveTab] = useState('families');
  const [families, setFamilies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStripeConfig, setShowStripeConfig] = useState(false);
  
  // Data State
  const [newFamily, setNewFamily] = useState({ name: '', adminEmail: '', adminName: '' });
  const [stripeConfig, setStripeConfig] = useState({ yearlyUrl: '', biannualUrl: '', enabled: false });
  const [replyText, setReplyText] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    if (isSuperAdmin) {
      loadFamilies();
      loadRequests();
      loadTickets();
      loadStripeSettings();
    }
  }, [isSuperAdmin]);

  const loadRequests = async () => {
    // Simple select, RLS handles visibility
    const { data, error } = await supabase.from('registration_requests').select('*').order('created_at', { ascending: false });
    if (error) {
        console.error("Error loading requests:", error);
        setMessage({ type: 'error', text: 'Fehler beim Laden der Anfragen.' });
    }
    setRequests(data || []);
  };

  const loadTickets = async () => {
    // 1. Fetch tickets
    const { data: rawTickets, error } = await supabase
        .from('support_tickets')
        .select(`*, families(name)`)
        .order('created_at', { ascending: false });
    
    if (error) {
        console.error("Error loading tickets:", error);
        return;
    }

    if (!rawTickets || rawTickets.length === 0) {
        setTickets([]);
        return;
    }

    // 2. Fetch User Details manually (since we can't join auth.users or easily join family_members without explicit FK)
    const userIds = [...new Set(rawTickets.map(t => t.user_id))];
    const { data: members } = await supabase
        .from('family_members')
        .select('user_id, name, email')
        .in('user_id', userIds);

    // 3. Merge data
    const ticketsWithDetails = rawTickets.map(ticket => {
        const member = members?.find(m => m.user_id === ticket.user_id);
        return {
            ...ticket,
            senderName: member?.name || 'Unbekannt',
            senderEmail: member?.email || 'N/A'
        };
    });

    setTickets(ticketsWithDetails);
  };

  const loadStripeSettings = async () => {
    const { data } = await supabase.from('app_settings').select('value').eq('key', 'public_stripe_config').single();
    if (data?.value) setStripeConfig(data.value);
  };

  const saveStripeSettings = async () => {
      try {
          const { error } = await supabase.from('app_settings').upsert({
              key: 'public_stripe_config', value: stripeConfig, updated_by: user.id
          });
          if (error) throw error;
          setMessage({ type: 'success', text: 'Stripe Config gespeichert!' });
          setShowStripeConfig(false);
      } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  const loadFamilies = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('families').select(`*, family_members(count)`).order('created_at', { ascending: false });
    if (error) console.error("Error loading families:", error);
    setFamilies(data || []);
    setLoading(false);
  };

  const handleExtendSubscription = async (familyId, days) => {
      if(!confirm(`Abo um ${days} Tage verlängern?`)) return;
      try {
          const family = families.find(f => f.id === familyId);
          const currentEnd = family.subscription_end ? new Date(family.subscription_end) : new Date();
          const baseDate = currentEnd > new Date() ? currentEnd : new Date();
          const newEnd = addDays(baseDate, days).toISOString();

          await supabase.from('families').update({ subscription_end: newEnd, subscription_status: 'active' }).eq('id', familyId);
          loadFamilies();
          setMessage({ type: 'success', text: 'Abo verlängert.' });
      } catch(e) { setMessage({ type: 'error', text: e.message }); }
  };
  
  const handleToggleStatus = async (family) => {
      const newStatus = family.status === 'suspended' ? 'active' : 'suspended';
      if(!confirm(`Soll die Familie "${family.name}" wirklich auf "${newStatus}" gesetzt werden?`)) return;
      
      try {
          const { error } = await supabase.from('families').update({ status: newStatus }).eq('id', family.id);
          if (error) throw error;
          loadFamilies();
          setMessage({ type: 'success', text: `Status auf ${newStatus} geändert.` });
      } catch(e) { setMessage({ type: 'error', text: e.message }); }
  };
  
  const handleDeleteFamily = async (familyId) => {
      if(!confirm("WARNUNG: Diese Familie und ALLE Daten werden unwiderruflich gelöscht. Fortfahren?")) return;
      
      try {
          const { error } = await supabase.from('families').delete().eq('id', familyId);
          if (error) throw error;
          loadFamilies();
          setMessage({ type: 'success', text: 'Familie gelöscht.' });
      } catch(e) { setMessage({ type: 'error', text: e.message }); }
  };

  const handleCreateFamily = async (e) => {
      e.preventDefault();
      try {
          const { data: family, error: famError } = await supabase.from('families').insert({
              name: newFamily.name, admin_email: newFamily.adminEmail, status: 'active',
              subscription_plan: 'trial', subscription_end: addDays(new Date(), 30).toISOString(),
              approved_at: new Date(), approved_by: user.id
          }).select().single();
          if (famError) throw famError;

          await supabase.from('family_members').insert({
              family_id: family.id, name: newFamily.adminName, email: newFamily.adminEmail, role: 'admin', color: '#3B82F6', avatar: newFamily.adminName.charAt(0).toUpperCase()
          });

          setMessage({ type: 'success', text: 'Familie angelegt & 30 Tage Trial.' });
          setShowCreateModal(false);
          setNewFamily({ name: '', adminEmail: '', adminName: '' });
          loadFamilies();
      } catch (err) { setMessage({ type: 'error', text: err.message }); }
  };

  const handleApproveRequest = async (req) => {
     try {
        // 1. Create Family
        const { data: family, error: famError } = await supabase.from('families').insert({
            name: req.family_name, admin_email: req.email, status: 'active',
            subscription_plan: 'trial', subscription_end: addDays(new Date(), 30).toISOString(),
            approved_at: new Date(), approved_by: user.id
        }).select().single();
        
        if (famError) throw famError;

        // 2. Create Admin Member
        await supabase.from('family_members').insert({
            family_id: family.id, name: req.admin_name, email: req.email, role: 'admin', color: '#3B82F6', avatar: req.admin_name.charAt(0).toUpperCase()
        });
        
        // 3. Update Request Status
        await supabase.from('registration_requests').update({ status: 'approved' }).eq('id', req.id);
        
        setMessage({ type: 'success', text: 'Genehmigt & Trial gestartet. Nutzer muss sich nun registrieren/einloggen.' });
        loadRequests(); loadFamilies();
     } catch(e) { setMessage({ type: 'error', text: e.message }); }
  };
  
  const handleDeleteRequest = async (id) => {
      if(!confirm("Anfrage löschen?")) return;
      await supabase.from('registration_requests').delete().eq('id', id);
      loadRequests();
  };

  const handleReplyTicket = async (ticket) => {
      if(!replyText.trim()) return;
      try {
          const { error } = await supabase.from('support_tickets').update({
              admin_reply: replyText,
              status: 'replied',
              updated_at: new Date()
          }).eq('id', ticket.id);
          
          if (error) throw error;

          setReplyText('');
          setSelectedTicket(null);
          loadTickets();
          setMessage({ type: 'success', text: 'Antwort gesendet.' });
      } catch(e) { setMessage({ type: 'error', text: e.message }); }
  };

  if (!isSuperAdmin) return <div className="p-8 text-center">Zugriff verweigert</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between mb-8">
        <div className="flex items-center space-x-3">
          <div className="bg-purple-100 p-3 rounded-xl"><SafeIcon icon={FiShield} className="w-8 h-8 text-purple-600" /></div>
          <div><h1 className="text-3xl font-bold">Superadmin</h1><p className="text-gray-600">Abo & Familien Management</p></div>
        </div>
        <div className="flex gap-3">
            <button onClick={() => setShowStripeConfig(true)} className="flex items-center px-4 py-2 border rounded-lg hover:bg-gray-50"><SafeIcon icon={FiCreditCard} className="w-5 h-5 mr-2" /> Preise</button>
            <button onClick={() => setShowCreateModal(true)} className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"><SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" /> Neu</button>
        </div>
      </div>

      {message.text && <div className={`mb-4 p-3 rounded-lg ${message.type==='error'?'bg-red-100 text-red-700':'bg-green-100 text-green-700'}`}>{message.text}</div>}
      
      <div className="flex space-x-6 border-b border-gray-200 mb-6">
          <button onClick={() => setActiveTab('families')} className={`pb-3 px-2 font-medium ${activeTab==='families'?'text-purple-600 border-b-2 border-purple-600':'text-gray-500'}`}>Familien</button>
          <button onClick={() => setActiveTab('requests')} className={`pb-3 px-2 font-medium ${activeTab==='requests'?'text-purple-600 border-b-2 border-purple-600':'text-gray-500'}`}>Anfragen ({requests.filter(r=>r.status==='pending').length})</button>
          <button onClick={() => setActiveTab('tickets')} className={`pb-3 px-2 font-medium ${activeTab==='tickets'?'text-purple-600 border-b-2 border-purple-600':'text-gray-500'}`}>Support ({tickets.filter(t=>t.status==='open').length})</button>
      </div>

      {activeTab === 'families' && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Familie</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Abo Ende</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right">Aktionen</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {families.map(f => (
                        <tr key={f.id} className={f.status === 'suspended' ? 'bg-red-50' : ''}>
                            <td className="px-6 py-4">
                                <div className="font-medium">{f.name}</div>
                                <div className="text-xs text-gray-500">{f.admin_email}</div>
                            </td>
                            <td className="px-6 py-4 text-sm">
                                {f.subscription_end ? format(new Date(f.subscription_end), 'dd.MM.yyyy') : '-'}
                            </td>
                            <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${f.status === 'suspended' ? 'bg-red-200 text-red-800' : (new Date(f.subscription_end) < new Date() ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800')}`}>
                                    {f.status === 'suspended' ? 'Pausiert' : (new Date(f.subscription_end) < new Date() ? 'Abgelaufen' : 'Aktiv')}
                                </span>
                            </td>
                            <td className="px-6 py-4 text-right flex justify-end gap-2 items-center">
                                <button onClick={() => handleExtendSubscription(f.id, 30)} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded border hover:bg-blue-100">+1M</button>
                                <button onClick={() => handleExtendSubscription(f.id, 365)} className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded border hover:bg-purple-100">+1J</button>
                                <div className="h-4 w-px bg-gray-300 mx-1"></div>
                                <button onClick={() => handleToggleStatus(f)} className="text-gray-500 hover:text-gray-800" title={f.status==='suspended'?'Aktivieren':'Pausieren'}>
                                   <SafeIcon icon={f.status==='suspended' ? FiPlayCircle : FiPauseCircle} className="w-5 h-5" />
                                </button>
                                <button onClick={() => handleDeleteFamily(f.id)} className="text-red-400 hover:text-red-600" title="Löschen">
                                   <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
      )}

      {activeTab === 'requests' && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
             {requests.length === 0 && <div className="p-8 text-center text-gray-500">Keine Anfragen.</div>}
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right">Aktionen</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {requests.map(r => (
                        <tr key={r.id}>
                            <td className="px-6 py-4 text-sm text-gray-500">
                                {format(new Date(r.created_at), 'dd.MM.yyyy HH:mm')}
                            </td>
                            <td className="px-6 py-4">
                                <div className="font-bold">{r.family_name}</div>
                                <div className="text-sm">{r.admin_name} ({r.email})</div>
                            </td>
                            <td className="px-6 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${r.status==='pending'?'bg-yellow-100 text-yellow-800':'bg-green-100 text-green-800'}`}>
                                    {r.status}
                                </span>
                            </td>
                            <td className="px-6 py-4 text-right flex justify-end gap-2">
                                {r.status === 'pending' && (
                                    <button onClick={() => handleApproveRequest(r)} className="text-green-600 hover:text-green-800 bg-green-50 p-2 rounded">
                                        <SafeIcon icon={FiCheck} className="w-5 h-5" />
                                    </button>
                                )}
                                <button onClick={() => handleDeleteRequest(r.id)} className="text-red-400 hover:text-red-600 bg-red-50 p-2 rounded">
                                   <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
      )}

      {activeTab === 'tickets' && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {tickets.length === 0 && <div className="p-8 text-center text-gray-500">Keine Support-Anfragen.</div>}
          <div className="divide-y divide-gray-200">
             {tickets.map(ticket => (
               <div key={ticket.id} className="p-6">
                 <div className="flex justify-between items-start mb-2">
                    <div>
                        <h4 className="font-bold text-lg">{ticket.subject}</h4>
                        <div className="text-sm text-gray-500">
                           Von: {ticket.families?.name} ({ticket.senderName} - {ticket.senderEmail}) am {format(new Date(ticket.created_at), 'dd.MM.yyyy HH:mm')}
                        </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      ticket.status === 'open' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {ticket.status.toUpperCase()}
                    </span>
                 </div>
                 <p className="text-gray-700 bg-gray-50 p-3 rounded-lg mb-4">{ticket.message}</p>
                 
                 {ticket.admin_reply && (
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 mb-4">
                        <div className="text-xs text-blue-600 font-bold mb-1">Antwort:</div>
                        {ticket.admin_reply}
                    </div>
                 )}

                 {selectedTicket === ticket.id ? (
                    <div className="mt-4">
                        <textarea 
                           className="w-full border p-2 rounded mb-2" 
                           rows="3" 
                           placeholder="Antwort schreiben..."
                           value={replyText}
                           onChange={e => setReplyText(e.target.value)}
                        ></textarea>
                        <div className="flex gap-2">
                           <button onClick={() => handleReplyTicket(ticket)} className="bg-blue-600 text-white px-4 py-2 rounded">Senden</button>
                           <button onClick={() => setSelectedTicket(null)} className="text-gray-500 px-4 py-2">Abbrechen</button>
                        </div>
                    </div>
                 ) : (
                    <button onClick={() => { setSelectedTicket(ticket.id); setReplyText(ticket.admin_reply || ''); }} className="text-blue-600 text-sm font-bold flex items-center">
                       <SafeIcon icon={FiMessageSquare} className="mr-1" /> {ticket.admin_reply ? 'Antwort bearbeiten' : 'Antworten'}
                    </button>
                 )}
               </div>
             ))}
          </div>
        </div>
      )}
      
      {/* Stripe Modal */}
      {showStripeConfig && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-xl p-6 max-w-md w-full">
                  <h3 className="text-xl font-bold mb-4">Preis-Konfiguration</h3>
                  <div className="space-y-4">
                      <div>
                          <label className="block text-sm font-bold mb-1">Jahreslink (39€)</label>
                          <input type="text" value={stripeConfig.yearlyUrl} onChange={e=>setStripeConfig({...stripeConfig, yearlyUrl: e.target.value})} className="w-full border rounded p-2" />
                      </div>
                      <div>
                          <label className="block text-sm font-bold mb-1">2-Jahreslink (64€)</label>
                          <input type="text" value={stripeConfig.biannualUrl} onChange={e=>setStripeConfig({...stripeConfig, biannualUrl: e.target.value})} className="w-full border rounded p-2" />
                      </div>
                      <button onClick={saveStripeSettings} className="w-full bg-purple-600 text-white rounded py-2 font-bold">Speichern</button>
                      <button onClick={()=>setShowStripeConfig(false)} className="w-full text-gray-500 mt-2">Abbrechen</button>
                  </div>
              </div>
          </div>
      )}
      
      {/* Create Family Modal */}
      {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-xl p-6 max-w-md w-full">
                  <h3 className="text-xl font-bold mb-4">Familie anlegen</h3>
                  <form onSubmit={handleCreateFamily} className="space-y-3">
                      <input placeholder="Name" className="w-full border p-2 rounded" value={newFamily.name} onChange={e=>setNewFamily({...newFamily, name:e.target.value})} required />
                      <input placeholder="Admin Name" className="w-full border p-2 rounded" value={newFamily.adminName} onChange={e=>setNewFamily({...newFamily, adminName:e.target.value})} required />
                      <input placeholder="Email" className="w-full border p-2 rounded" value={newFamily.adminEmail} onChange={e=>setNewFamily({...newFamily, adminEmail:e.target.value})} required />
                      <button className="w-full bg-purple-600 text-white py-2 rounded font-bold">Anlegen (Inkl. Trial)</button>
                      <button type="button" onClick={()=>setShowCreateModal(false)} className="w-full text-gray-500 mt-2">Abbrechen</button>
                  </form>
              </div>
          </div>
      )}
    </div>
  );
}